import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Button, Spinner } from "reactstrap";
import { FiArrowLeft } from "react-icons/fi";
import { observer, inject } from "mobx-react";
import { motion } from "framer-motion";
import IframeResizer from "iframe-resizer-react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

import { postVariants } from "../../components/util";
import {
  STUDIO_PLUS_PAYMENT_COMPLETE_EVENT,
  buildStudioPlusPayUrl,
  createStudioPlusPaymentSession,
  getHostPageBackgroundColor,
  getStudioPlusPaymentsOrigin,
  isStudioPlusPaymentsMessage,
  isStudioPlusPaymentsProvider,
  isStudioPlusPaymentsSuccessStatus,
  pollStudioPlusPaymentStatus,
} from "../../components/payments/studioPlusPayments";

const STUDIO_PLUS_PAYMENT_RESIZE_EVENT = "payment-resize";
const MIN_STUDIO_PLUS_IFRAME_HEIGHT = 320;
const DEFAULT_STUDIO_PLUS_IFRAME_HEIGHT = 760;
const MIN_INITIAL_STUDIO_PLUS_IFRAME_HEIGHT = 680;
const MAX_INITIAL_STUDIO_PLUS_IFRAME_HEIGHT = 900;

function getInitialStudioPlusIframeHeight() {
  if (typeof window === "undefined") {
    return DEFAULT_STUDIO_PLUS_IFRAME_HEIGHT;
  }

  return Math.max(
    MIN_INITIAL_STUDIO_PLUS_IFRAME_HEIGHT,
    Math.min(
      MAX_INITIAL_STUDIO_PLUS_IFRAME_HEIGHT,
      window.innerHeight - 180
    )
  );
}

const PaymentPage: React.FC<any> = ({
  authStore,
  navigationStore,
  ecomStore,
}) => {
  const { t } = useTranslation();
  const [iframeUrl, setIframeUrl] = useState("");
  const [loadingIframe, setLoadingIframe] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [hasStudioPlusMeasuredHeight, setHasStudioPlusMeasuredHeight] = useState(
    false
  );
  const [studioPlusIframeHeight, setStudioPlusIframeHeight] = useState(
    getInitialStudioPlusIframeHeight()
  );
  const paymentSessionIdRef = useRef("");
  const paymentContainerRef = useRef<HTMLDivElement | null>(null);
  const studioPlusIframeRef = useRef<HTMLIFrameElement | null>(null);
  const studioPlusPaymentsOrigin = getStudioPlusPaymentsOrigin(
    authStore?.studioPlusPaymentsWebURL
  );

  const handleStudioPlusFrameLoad = () => {
    const iframeWindow = studioPlusIframeRef.current?.contentWindow;

    if (!iframeWindow || !studioPlusPaymentsOrigin) {
      return;
    }

    const backgroundColor = getHostPageBackgroundColor(paymentContainerRef.current);

    iframeWindow.postMessage(
      {
        type: "sp-pay-parent-handshake",
      },
      studioPlusPaymentsOrigin
    );

    if (backgroundColor) {
      iframeWindow.postMessage(
        {
          type: "theme",
          backgroundColor,
        },
        studioPlusPaymentsOrigin
      );
    }
  };

  useEffect(() => {
    if (!isStudioPlusPaymentsProvider(authStore)) {
      const handleLegacyMessage = (event: MessageEvent) => {
        if (
          typeof event.data === "string" &&
          event.data.toUpperCase() === "SUCCESS"
        ) {
          ecomStore.onCheckout();
          navigationStore.push("/checkout/confirmation");
        }
      };

      window.addEventListener("message", handleLegacyMessage);
      return () => {
        window.removeEventListener("message", handleLegacyMessage);
      };
    }

    let disposed = false;

    const loadPaymentSession = async () => {
      if (!ecomStore?.cart?.Key || !authStore?.studioPlusPaymentsWebURL) {
        return;
      }

      setLoadingIframe(true);
      setPaymentError("");

      try {
        const paymentSession = await createStudioPlusPaymentSession(
          ecomStore.cart.Key
        );

        if (disposed) {
          return;
        }

        paymentSessionIdRef.current = paymentSession?.PaymentSessionId || "";
        setHasStudioPlusMeasuredHeight(false);
        setStudioPlusIframeHeight(getInitialStudioPlusIframeHeight());
        setIframeUrl(
          buildStudioPlusPayUrl(
            authStore.studioPlusPaymentsWebURL,
            paymentSession.PaymentSessionId
          )
        );
      } catch {
        if (!disposed) {
          setPaymentError("Unable to open the payment page. Please try again.");
        }
      } finally {
        if (!disposed) {
          setLoadingIframe(false);
        }
      }
    };

    const handleStudioPlusMessage = async (event: MessageEvent) => {
      if (
        event.origin === studioPlusPaymentsOrigin &&
        typeof event.data === "object" &&
        event.data?.type === STUDIO_PLUS_PAYMENT_RESIZE_EVENT
      ) {
        const requestedHeight = Number(
          event.data?.height ??
            event.data?.iframeHeight ??
            event.data?.contentHeight
        );

        if (Number.isFinite(requestedHeight) && requestedHeight > 0) {
          setStudioPlusIframeHeight(
            Math.max(MIN_STUDIO_PLUS_IFRAME_HEIGHT, Math.ceil(requestedHeight))
          );
          setHasStudioPlusMeasuredHeight(true);
        }

        return;
      }

      if (
        typeof event.data === "string" &&
        event.data.toUpperCase() === "SUCCESS"
      ) {
        ecomStore.onCheckout();
        navigationStore.push("/checkout/confirmation");
        return;
      }

      if (
        !isStudioPlusPaymentsMessage(
          event,
          authStore.studioPlusPaymentsWebURL,
          STUDIO_PLUS_PAYMENT_COMPLETE_EVENT
        ) ||
        !paymentSessionIdRef.current
      ) {
        return;
      }

      if (isStudioPlusPaymentsSuccessStatus(event.data?.paymentIntentStatus)) {
        ecomStore.onCheckout();
        navigationStore.push("/checkout/confirmation");
        return;
      }

      setLoadingIframe(true);
      setPaymentError("");

      try {
        const paymentStatus = await pollStudioPlusPaymentStatus(
          paymentSessionIdRef.current
        );
        if (isStudioPlusPaymentsSuccessStatus(paymentStatus?.Status)) {
          ecomStore.onCheckout();
          navigationStore.push("/checkout/confirmation");
          return;
        }

        setPaymentError("Payment was not completed. Please try again.");
      } catch {
        setPaymentError("We could not verify the payment status. Please try again.");
      } finally {
        setLoadingIframe(false);
      }
    };

    loadPaymentSession();
    window.addEventListener("message", handleStudioPlusMessage);

    return () => {
      disposed = true;
      window.removeEventListener("message", handleStudioPlusMessage);
    };
  }, [authStore, ecomStore, navigationStore]);

  return (
    <>
      <Helmet>
        <title>
          {t("PaymentPage.urlTitle")}{" "}
          {authStore.companyName || authStore.companyName !== ""
            ? "| " + authStore.companyName
            : " "}
        </title>
      </Helmet>
      <motion.div
        initial="initial"
        animate="enter"
        exit="exit"
        variants={postVariants}
      >
        <div
          ref={paymentContainerRef}
          className="cartIframe cartIframe--paymentDocked"
        >
          {loadingIframe && (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          )}
          {!!paymentError && (
            <p className="text-danger text-center py-3 mb-0">{paymentError}</p>
          )}
          {!loadingIframe &&
            (!!iframeUrl || !isStudioPlusPaymentsProvider(authStore)) && (
              isStudioPlusPaymentsProvider(authStore) ? (
                <iframe
                  ref={studioPlusIframeRef}
                  title="Studio Plus payment"
                  src={iframeUrl}
                  onLoad={handleStudioPlusFrameLoad}
                  scrolling={hasStudioPlusMeasuredHeight ? "no" : "yes"}
                  style={{
                    width: "100%",
                    height: studioPlusIframeHeight,
                    minHeight: MIN_STUDIO_PLUS_IFRAME_HEIGHT,
                    border: 0,
                    display: "block",
                  }}
                />
              ) : (
                <IframeResizer
                  log={false}
                  scrolling={false}
                  src={ecomStore.cart.PaymentUrl}
                  style={{
                    width: 1,
                    minWidth: "100%",
                    minHeight: 1000,
                    border: 0,
                  }}
                />
              )
            )}
        </div>
      </motion.div>

      <Row className="align-items-center paymentPagePreviousLinkRow">
        <Col sm="6" className="text-center text-sm-right order-sm-2"></Col>
        <Col
          sm="6"
          className="text-center text-sm-left order-sm-1 mt-3 mt-sm-0"
        >
          <Button
            type="button"
            color="default-link"
            onClick={() => navigationStore.replace("/checkout/orderreview")}
          >
            <FiArrowLeft className="mr-2" />
            {`Previous page`}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default inject(
  "authStore",
  "navigationStore",
  "ecomStore"
)(observer(PaymentPage));
